/**
 * Decorated join QR code (qr-code-styling), rendered as SVG that fills its box.
 *
 * Error correction stays at "H" so the rounded modules and gradients keep the
 * code scannable. A thin white margin is kept as the quiet zone scanners need
 * to find the code against the dark TV background.
 */

import { useEffect, useRef } from 'react'
import QRCodeStyling, { type Options } from 'qr-code-styling'

interface StyledQrCodeProps {
  value: string
}

/** Intrinsic size only; CSS scales the SVG to the container. */
const RENDER_SIZE = 600

function buildOptions(value: string): Options {
  return {
    type: 'svg',
    shape: 'square',
    width: RENDER_SIZE,
    height: RENDER_SIZE,
    data: value,
    margin: 24,
    qrOptions: { errorCorrectionLevel: 'H' },
    dotsOptions: {
      type: 'classy-rounded',
      gradient: {
        type: 'radial',
        colorStops: [
          { offset: 0, color: '#4c0099' },
          { offset: 0.5, color: '#a3006d' },
          { offset: 1, color: '#00505e' },
        ],
      },
    },
    cornersSquareOptions: {
      type: 'extra-rounded',
      gradient: {
        type: 'linear',
        rotation: Math.PI / 4,
        colorStops: [
          { offset: 0, color: '#b30047' },
          { offset: 1, color: '#2a0066' },
        ],
      },
    },
    cornersDotOptions: {
      type: 'dots',
      color: '#00505e',
    },
    backgroundOptions: { color: '#ffffff' },
  }
}

export function StyledQrCode({ value }: StyledQrCodeProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (container === null) return
    new QRCodeStyling(buildOptions(value)).append(container)
    return () => container.replaceChildren()
  }, [value])

  return (
    <div
      ref={containerRef}
      className="h-full w-full [&>svg]:h-full [&>svg]:w-full"
    />
  )
}
